/**
 * `office:presentation` parsing and surgical text-box rewriting.
 *
 * The output is the same {@link SlideShape}/{@link SlideParagraph} shape the
 * PowerPoint reader produces, so the slide stage, the thumbnail rail, the notes
 * pane, the search and the outline all apply to an ODP unchanged — and §17's
 * guarantee comes with it for free: the slide viewer has no animation code, so
 * "transitions are preserved but not played" is a property of the renderer
 * rather than a flag. ODF's `presentation:animations` are copied through on
 * save and there is nothing here that could start them moving.
 *
 * ODF places shapes the way PresentationML does — absolute coordinates on a
 * fixed canvas — which is the one document type where absolute positioning is
 * the honest rendering. A `draw:frame` carries `svg:x`/`svg:y`/`svg:width`/
 * `svg:height`; a `draw:text-box` inside it holds the paragraphs; a
 * `draw:image` inside it is a picture. Coordinates are converted to the CSS
 * pixels the shared viewer already works in.
 *
 * The save path rewrites only the paragraphs of an edited text box and copies
 * every other byte of the package — animations, master pages, the graphic
 * styles that colour a shape — through untouched (§13, §24).
 */
import type { SlideParagraph, SlideRun, SlideShape } from '../pptx/slide-xml.js';
import { escapeXml } from '../ooxml/xml.js';
import { attr, childElements, findElement, intAttr, lengthToPx, tokenize } from './scan.js';
import type { ElementSpan } from './scan.js';
import { resolveText } from './styles.js';
import type { OdfStyleTable } from './styles.js';
import { resolveHref } from './package.js';

export interface OdfSlide {
  readonly index: number;
  /** Synthetic part key, so the shared per-slide edit map has something to key on. */
  readonly part: string;
  readonly name: string;
  readonly shapes: readonly SlideShape[];
  readonly notes: string;
  readonly title: string;
  readonly hidden: boolean;
}

export interface ParsedOdp {
  readonly slides: readonly OdfSlide[];
  readonly width: number;
  readonly height: number;
  /** Picture parts referenced by any slide, first-use order. */
  readonly imageParts: readonly string[];
  readonly externalRefs: readonly string[];
  readonly truncated: boolean;
}

export interface OdpParseOptions {
  readonly styles: OdfStyleTable;
  readonly maxShapes: number;
  /** Page dimensions in CSS pixels, from the master page's layout. */
  readonly width: number;
  readonly height: number;
}

/* ------------------------------------------------------------------ *
 * pages
 * ------------------------------------------------------------------ */

export function parseOdp(xml: string, options: OdpParseOptions): ParsedOdp {
  const body = findPresentationBody(xml);
  const slides: OdfSlide[] = [];
  const imageParts: string[] = [];
  const externalRefs: string[] = [];
  let truncated = false;

  if (body) {
    for (const page of childElements(xml, body, ['draw:page'])) {
      const parsed = parsePage(xml, page, slides.length, options, imageParts, externalRefs);
      slides.push(parsed.slide);
      if (parsed.truncated) truncated = true;
    }
  }

  return {
    slides,
    width: options.width,
    height: options.height,
    imageParts,
    externalRefs,
    truncated,
  };
}

interface PageResult {
  readonly slide: OdfSlide;
  readonly truncated: boolean;
}

function parsePage(
  xml: string, page: ElementSpan, index: number, options: OdpParseOptions,
  imageParts: string[], externalRefs: string[],
): PageResult {
  const shapes: SlideShape[] = [];
  let truncated = false;
  let notes = '';

  const frames = childElements(xml, page, [
    'draw:frame', 'draw:custom-shape', 'draw:rect', 'draw:ellipse',
    'draw:line', 'draw:polygon', 'draw:polyline', 'draw:connector',
    'draw:g', 'table:table', 'presentation:notes',
  ]);

  for (const frame of frames) {
    if (frame.name === 'presentation:notes') { notes = readNotes(xml, frame); continue; }
    if (shapes.length >= options.maxShapes) { truncated = true; break; }
    const shape = parseFrame(xml, frame, shapes.length, options, imageParts, externalRefs);
    if (shape) shapes.push(shape);
  }

  const titleShape = shapes.find((s) =>
    s.kind === 'text' && (s.placeholder === 'title' || s.placeholder === 'ctrTitle'));
  const title = titleShape && titleShape.kind === 'text' ? firstLine(titleShape.text) : '';

  return {
    slide: {
      index,
      part: `content.xml#page${index}`,
      name: attr(page.attrs, 'draw:name') ?? `Slide ${index + 1}`,
      shapes,
      notes,
      title,
      // ODF marks a hidden slide on its own display style, not the page; the
      // common inline form is a presentation:visibility attribute.
      hidden: attr(page.attrs, 'presentation:visibility') === 'hidden',
    },
    truncated,
  };
}

/* ------------------------------------------------------------------ *
 * frames
 * ------------------------------------------------------------------ */

function parseFrame(
  xml: string, frame: ElementSpan, indexInPage: number, options: OdpParseOptions,
  imageParts: string[], externalRefs: string[],
): SlideShape | null {
  const id = attr(frame.attrs, 'draw:id') ?? attr(frame.attrs, 'xml:id') ?? `shp${indexInPage}`;
  const name = attr(frame.attrs, 'draw:name') ?? '';
  const rect = {
    x: lengthToPx(attr(frame.attrs, 'svg:x')) ?? 0,
    y: lengthToPx(attr(frame.attrs, 'svg:y')) ?? 0,
    width: lengthToPx(attr(frame.attrs, 'svg:width')) ?? 0,
    height: lengthToPx(attr(frame.attrs, 'svg:height')) ?? 0,
  };
  const presentationClass = attr(frame.attrs, 'presentation:class');
  const placeholder = mapPlaceholder(presentationClass);

  // A picture: a draw:image, directly or wrapped in the frame.
  const image = childElements(xml, frame, ['draw:image'])[0];
  if (image) {
    const href = attr(image.attrs, 'xlink:href');
    const part = resolveHref(href);
    if (part) imageParts.push(part);
    else if (href) externalRefs.push(href);
    return {
      kind: 'picture', id, name, rect, rotation: 0,
      relId: href ?? '', part,
      alt: readSvgTitle(xml, frame) || name,
      start: frame.start, end: frame.end,
    };
  }

  // A table frame: a table:table, directly or wrapped.
  const table = frame.name === 'table:table'
    ? frame
    : childElements(xml, frame, ['table:table'])[0];
  if (table) {
    return {
      kind: 'frame', id, name, rect, rotation: 0,
      content: 'table', rows: readTableRows(xml, table),
      start: frame.start, end: frame.end,
    };
  }

  // Object frames — charts, embedded documents, plugins — are preserved but
  // not rendered.
  const object = childElements(xml, frame, ['draw:object', 'draw:object-ole', 'draw:plugin'])[0];
  if (object) {
    return {
      kind: 'frame', id, name, rect, rotation: 0,
      content: /chart/.test(attr(object.attrs, 'xlink:href') ?? '') ? 'chart' : 'object',
      rows: [], start: frame.start, end: frame.end,
    };
  }

  // A text box: draw:text-box, or — for the shape primitives — text children
  // directly under the shape.
  const box = childElements(xml, frame, ['draw:text-box'])[0];
  const textHost = box ?? (frame.name.startsWith('draw:') && frame.name !== 'draw:frame' ? frame : null);
  if (!textHost) return null;

  const scan = readParagraphs(xml, textHost, options.styles);
  const text = scan.paragraphs.map((p) => p.text).join('\n');

  return {
    kind: 'text',
    id, name, rect, rotation: 0,
    placeholder,
    paragraphs: scan.paragraphs,
    text,
    originalText: text,
    // Presentation frames are anchored to the middle by default in ODF's
    // rendering; a body list reads better from the top.
    anchor: placeholder === 'title' || placeholder === 'ctrTitle' || placeholder === 'subTitle'
      ? 'middle' : 'top',
    editable: scan.editable,
    start: frame.start, end: frame.end,
  };
}

interface ParagraphScan {
  readonly paragraphs: SlideParagraph[];
  readonly editable: boolean;
}

function readParagraphs(
  xml: string, host: ElementSpan, styles: OdfStyleTable,
): ParagraphScan {
  const paragraphs: SlideParagraph[] = [];
  let editable = true;

  for (const p of childElements(xml, host, ['text:p', 'text:h'])) {
    const paragraphStyle = attr(p.attrs, 'text:style-name');
    const runs: SlideRun[] = [];
    const styleStack: (string | null)[] = [paragraphStyle];
    let depth = 0;

    const push = (t: string): void => {
      if (!t) return;
      const style = resolveText(styles, styleStack);
      runs.push({
        text: t,
        bold: style.bold, italic: style.italic, underline: style.underline,
        sizePx: style.sizePx, color: style.color, href: null,
      });
    };

    for (const token of tokenize(xml, p.innerStart, p.innerEnd)) {
      if (depth > 0) {
        if (token.kind === 'open' && token.name === 'office:annotation') depth += 1;
        else if (token.kind === 'close' && token.name === 'office:annotation') depth -= 1;
        continue;
      }
      if (token.kind === 'text') { push(token.text); continue; }
      if (token.kind === 'open' && token.name === 'text:span') styleStack.push(attr(token.attrs, 'text:style-name'));
      else if (token.kind === 'close' && token.name === 'text:span') styleStack.pop();
      else if (token.name === 'text:s') push(' '.repeat(Math.max(1, intAttr(token.attrs, 'text:c', 1))));
      else if (token.name === 'text:tab') push('\t');
      else if (token.name === 'text:line-break') runs.push({ text: '\n', bold: false, italic: false, underline: false, sizePx: null, color: null, href: null });
      else if (token.kind === 'open' && token.name === 'office:annotation') { editable = false; depth += 1; }
      else if (token.kind === 'open' && (token.name === 'text:a' || token.name.startsWith('text:'))) {
        // A hyperlink's text is kept; a field or note makes the paragraph
        // unrebuildable, which the outer editable flag records for the box.
        if (token.name !== 'text:a' && token.name !== 'text:span') editable = false;
      }
    }

    const align = alignmentOf(styles, paragraphStyle);
    paragraphs.push({
      runs,
      text: runs.map((r) => r.text).join(''),
      level: 0,
      alignment: align,
      bulleted: p.name === 'text:p',
    });
  }

  return { paragraphs, editable };
}

function alignmentOf(
  styles: OdfStyleTable, name: string | null,
): 'left' | 'center' | 'right' | 'justify' | null {
  if (!name) return null;
  const style = styles.styles.get(name);
  return style?.paragraph.alignment ?? null;
}

function mapPlaceholder(cls: string | null): string | null {
  switch (cls) {
    case 'title': return 'title';
    case 'outline':
    case 'text': return 'body';
    case 'subtitle': return 'subTitle';
    case 'notes': return 'body';
    case null:
    case undefined: return null;
    default: return cls;
  }
}

/* ------------------------------------------------------------------ *
 * notes, titles, tables
 * ------------------------------------------------------------------ */

function readNotes(xml: string, notes: ElementSpan): string {
  const lines: string[] = [];
  for (const box of childElements(xml, notes, ['draw:frame'])) {
    const textBox = childElements(xml, box, ['draw:text-box'])[0];
    if (!textBox) continue;
    for (const p of childElements(xml, textBox, ['text:p', 'text:h'])) {
      lines.push(plainText(xml, p));
    }
  }
  return lines.join('\n').trim();
}

function readSvgTitle(xml: string, frame: ElementSpan): string {
  const title = childElements(xml, frame, ['svg:title', 'svg:desc'])[0];
  return title ? plainText(xml, title).trim() : '';
}

function readTableRows(xml: string, table: ElementSpan): string[][] {
  const rows: string[][] = [];
  for (const row of childElements(xml, table, ['table:table-row'])) {
    const cells: string[] = [];
    for (const cell of childElements(xml, row, ['table:table-cell', 'table:covered-table-cell'])) {
      const lines: string[] = [];
      for (const p of childElements(xml, cell, ['text:p', 'text:h'])) lines.push(plainText(xml, p));
      cells.push(lines.join(' '));
    }
    rows.push(cells);
  }
  return rows;
}

function plainText(xml: string, span: ElementSpan): string {
  let out = '';
  for (const token of tokenize(xml, span.innerStart, span.innerEnd)) {
    if (token.kind === 'text') out += token.text;
    else if (token.name === 'text:s') out += ' '.repeat(Math.max(1, intAttr(token.attrs, 'text:c', 1)));
    else if (token.name === 'text:tab') out += '\t';
  }
  return out;
}

function firstLine(text: string): string {
  const nl = text.indexOf('\n');
  return nl < 0 ? text : text.slice(0, nl);
}

/* ------------------------------------------------------------------ *
 * body and page geometry
 * ------------------------------------------------------------------ */

function findPresentationBody(xml: string): ElementSpan | null {
  const body = findElement(xml, 'office:body');
  if (!body) return null;
  return childElements(xml, body, ['office:presentation'])[0] ?? null;
}

/**
 * Page dimensions in CSS pixels, from the first `style:page-layout` in
 * `styles.xml`. A presentation's pages are all one size, so the first layout
 * is representative; the 4:3 fallback is what writers use when none is stated.
 */
export function readOdpPageSize(stylesXml: string): { width: number; height: number } {
  const props = /<style:page-layout-properties\b([^>]*)/.exec(stylesXml)?.[1] ?? '';
  const width = lengthToPx(attr(props, 'fo:page-width'));
  const height = lengthToPx(attr(props, 'fo:page-height'));
  if (width && height) return { width: Math.round(width), height: Math.round(height) };
  return { width: 960, height: 720 };
}

/* ------------------------------------------------------------------ *
 * editing
 * ------------------------------------------------------------------ */

export interface OdpShapeEdit {
  readonly id: string;
  readonly text: string;
}

export interface OdpEditResult {
  readonly xml: string;
  readonly dropped: readonly string[];
  readonly failed: readonly string[];
}

/**
 * Rewrite the text of specific shapes on one page.
 *
 * The frame element, its geometry, its style reference and the `draw:text-box`
 * wrapper are all kept; only the paragraph list inside the text box is
 * replaced. Each new paragraph carries the style name the first paragraph had,
 * so a body's font survives even though several differently-styled runs
 * collapse into one — which is reported rather than hidden (§13, §24).
 */
export function applyOdpShapeEdits(
  xml: string, shapes: readonly SlideShape[], edits: readonly OdpShapeEdit[],
): OdpEditResult {
  const byId = new Map(shapes.map((s) => [s.id, s]));
  const dropped: string[] = [];
  const failed: string[] = [];

  const ordered = [...edits].sort((a, b) => (byId.get(b.id)?.start ?? -1) - (byId.get(a.id)?.start ?? -1));

  let out = xml;
  for (const edit of ordered) {
    const shape = byId.get(edit.id);
    if (!shape || shape.kind !== 'text' || !shape.editable) { failed.push(edit.id); continue; }

    const source = out.slice(shape.start, shape.end);
    const boxOpen = source.indexOf('<draw:text-box');
    if (boxOpen < 0) { failed.push(edit.id); continue; }
    const boxOpenEnd = source.indexOf('>', boxOpen);
    const boxClose = source.indexOf('</draw:text-box>', boxOpenEnd);
    if (boxOpenEnd < 0 || boxClose < 0) { failed.push(edit.id); continue; }

    const inner = source.slice(boxOpenEnd + 1, boxClose);
    const firstStyle = /<text:(?:p|h)\b[^>]*\btext:style-name\s*=\s*"([^"]*)"/.exec(inner)?.[1] ?? null;
    const styleAttr = firstStyle ? ` text:style-name="${escapeXml(firstStyle)}"` : '';

    const formats = new Set(shape.paragraphs.flatMap((p) => p.runs)
      .map((r) => JSON.stringify([r.bold, r.italic, r.underline, r.sizePx, r.color])));
    if (formats.size > 1) {
      dropped.push(`도형 ${shape.name || shape.id}: 서로 다른 글자 서식이 첫 서식으로 통일됨`);
    }

    const paragraphs = edit.text.split('\n')
      .map((line) => `<text:p${styleAttr}>${encodeSlideText(line)}</text:p>`)
      .join('');

    const rebuilt = source.slice(0, boxOpenEnd + 1) + paragraphs + source.slice(boxClose);
    out = out.slice(0, shape.start) + rebuilt + out.slice(shape.end);
  }

  return { xml: out, dropped, failed };
}

/** One line of slide text; spaces collapse in ODF exactly as in the body. */
function encodeSlideText(line: string): string {
  const parts = line.split('\t').map((seg) => encodeSpaces(seg));
  return parts.join('<text:tab/>');
}

function encodeSpaces(segment: string): string {
  let out = '';
  let i = 0;
  while (i < segment.length) {
    if (segment[i] !== ' ') {
      const next = segment.indexOf(' ', i);
      const end = next < 0 ? segment.length : next;
      out += escapeXml(segment.slice(i, end));
      i = end;
      continue;
    }
    let run = 0;
    while (i + run < segment.length && segment[i + run] === ' ') run += 1;
    if (out === '') out += run === 1 ? '<text:s/>' : `<text:s text:c="${run}"/>`;
    else out += run === 1 ? ' ' : ` <text:s text:c="${run - 1}"/>`;
    i += run;
  }
  return out;
}
